import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Drawer, Input, InputNumber, Switch, Tooltip } from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  HolderOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dynamicFormsService from '../../services/dynamicFormsService';
import RichTextEditor from './RichTextEditor';
import LivePreview from './LivePreview';
import useElementWidth from './useElementWidth';
import { asPublicField, draftToPublicField, previewData } from './previewData';
import {
  FIELD_NAME_PATTERN,
  FIELD_TYPES,
  formatFieldName,
  isChoice,
  normalizeLabel,
  questionTitle,
  toFieldName,
  truthy,
  typeOf,
} from './fieldTypes';
import { ENGLISH, formStrings } from './formStrings';

const emptyDraft = (type) => ({
  field_id: null,
  field_type: type,
  display_label: '',
  field_name: '',
  nameTouched: false,
  field_description: '',
  is_required: false,
  isVisible: true,
  allowed_file_types: type === 'file' ? 'pdf,docx' : '',
  max_file_size_mb: type === 'file' ? 10 : null,
  options: isChoice(type) ? [{ label: '', max_pax: null }, { label: '', max_pax: null }] : [],
  allow_other: false,
  other_option_label: 'Other',
  is_workshop: false,
  min_selections: null,
  max_selections: null,
});

/** Boxes an applicant can tick: the options, plus "Other" when it is on. */
const pickableCount = (d) => d.options.filter((o) => o.label.trim()).length + (d.allow_other ? 1 : 0);

const capacityFor = (field, label) => {
  const cap = field.workshop_capacity || {};
  return cap[label] || cap[normalizeLabel(label)] || null;
};

/** A saved field as an editable draft. */
const draftFrom = (field) => ({
  field_id: field.field_id,
  field_type: field.field_type,
  display_label: field.display_label || '',
  field_name: field.field_name,
  nameTouched: true,
  field_description: field.field_description || '',
  is_required: truthy(field.is_required),
  isVisible: field.isVisible === undefined ? true : truthy(field.isVisible),
  allowed_file_types: field.allowed_file_types || '',
  max_file_size_mb: field.max_file_size_mb ?? null,
  options: (Array.isArray(field.field_options) ? field.field_options : []).map((label) => ({
    label,
    max_pax: capacityFor(field, label)?.max_pax ?? null,
  })),
  allow_other: truthy(field.allow_other),
  other_option_label: field.other_option_label || 'Other',
  is_workshop: truthy(field.is_workshop),
  min_selections: field.min_selections || null,
  max_selections: field.max_selections || null,
});

const comparable = (d) => (d ? JSON.stringify({ ...d, nameTouched: undefined }) : '');

const uniqueName = (base, taken) => {
  if (!base || !taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
};

const OutlineItem = ({ field, selected, onSelect, canEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.field_id, disabled: !canEdit });
  const type = typeOf(field.field_type);
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`dfw-outline__item${selected ? ' is-on' : ''}${isDragging ? ' is-dragging' : ''}`}
    >
      {canEdit && (
        <button type="button" className="dfw-outline__grip" aria-label={`Move ${questionTitle(field)}`} {...attributes} {...listeners}>
          <HolderOutlined aria-hidden="true" />
        </button>
      )}
      <button type="button" className="dfw-outline__pick" onClick={onSelect} aria-current={selected ? 'true' : undefined}>
        <span className="dfw-tile" aria-hidden="true">{type.icon}</span>
        <span className="dfw-outline__text">
          <span className="dfw-outline__title">
            {questionTitle(field)}
            {truthy(field.is_required) && <span className="dfw-req" aria-label="required">*</span>}
          </span>
          <span className="dfw-outline__type">
            {type.label}
            {truthy(field.is_workshop) && ' · places'}
            {!truthy(field.isVisible) && ' · title hidden'}
          </span>
        </span>
      </button>
    </li>
  );
};

const Toggle = ({ label, hint, checked, onChange, disabled }) => (
  <label className={`dfw-toggle${disabled ? ' is-disabled' : ''}`}>
    <Switch size="small" checked={checked} onChange={onChange} disabled={disabled} />
    <span>
      <span className="dfw-toggle__label">{label}</span>
      {hint && <span className="dfw-toggle__hint">{hint}</span>}
    </span>
  </label>
);

const FieldError = ({ id, text }) => (text ? <p className="dfw-error" id={id} role="alert">{text}</p> : null);

/**
 * Build a form's questions on the page: the list on the left, the question being edited in the
 * middle and the real form page on the right. Saving uses the same field and workshop calls as
 * before; saved questions are live on the public form straight away.
 *
 * fields: from GET /forms/:id/fields. onFieldsChange(): reload them after a save.
 */
const QuestionsBuilder = ({ formId, fields, onFieldsChange, canEdit = true, language, layout, liveUrl }) => {
  const { message, modal } = App.useApp();
  const rootRef = useRef(null);
  const width = useElementWidth(rootRef);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [focusField, setFocusField] = useState(null);

  const sorted = useMemo(() => [...fields].sort((a, b) => a.field_order - b.field_order), [fields]);
  const saved = selectedId && selectedId !== 'new' ? sorted.find((f) => f.field_id === selectedId) : null;
  const dirty = draft && (draft.field_id ? comparable(draft) !== comparable(draftFrom(saved || {})) : true);
  const nextOrder = sorted.reduce((max, f) => Math.max(max, Number(f.field_order) || 0), 0) + 1;

  // open the first question once the list arrives
  useEffect(() => {
    if (selectedId === null && sorted.length > 0) {
      setSelectedId(sorted[0].field_id);
      setDraft(draftFrom(sorted[0]));
    }
  }, [sorted, selectedId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const confirmDiscard = (then) => {
    if (!dirty || !canEdit) {
      then();
      return;
    }
    modal.confirm({
      title: 'Discard your changes?',
      content: 'The question you are editing has changes that are not saved.',
      okText: 'Discard changes',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: then,
      rootClassName: 'do-scope',
    });
  };

  const select = (field) => {
    if (field.field_id === selectedId) return;
    confirmDiscard(() => {
      setSelectedId(field.field_id);
      setDraft(draftFrom(field));
      setErrors({});
      setFocusField(field.field_name);
    });
  };

  const startNew = () => {
    confirmDiscard(() => {
      setSelectedId('new');
      setDraft(null);
      setErrors({});
    });
  };

  const takenNames = useMemo(
    () => new Set(sorted.filter((f) => f.field_id !== draft?.field_id).map((f) => f.field_name)),
    [sorted, draft?.field_id]
  );

  const update = (changes) => {
    setDraft((d) => {
      const next = { ...d, ...changes };
      if (!next.nameTouched && 'display_label' in changes) {
        next.field_name = uniqueName(toFieldName(changes.display_label), takenNames);
      }
      return next;
    });
    setErrors((e) => {
      const next = { ...e };
      Object.keys(changes).forEach((k) => delete next[k]);
      if ('display_label' in changes) delete next.field_name;
      return next;
    });
  };

  const pickType = (type) => {
    if (!draft) {
      setDraft(emptyDraft(type));
      setFocusField('__draft__');
      return;
    }
    const changes = { field_type: type };
    if (isChoice(type) && draft.options.length === 0) changes.options = [{ label: '', max_pax: null }, { label: '', max_pax: null }];
    if (type === 'file' && !draft.allowed_file_types) {
      changes.allowed_file_types = 'pdf,docx';
      changes.max_file_size_mb = draft.max_file_size_mb || 10;
    }
    update(changes);
  };

  const setOption = (index, changes) => update({ options: draft.options.map((o, i) => (i === index ? { ...o, ...changes } : o)) });

  // ---- validation, mirroring the old builder's rules and the backend's limits
  const validate = (d) => {
    const e = {};
    const choice = isChoice(d.field_type);
    const labels = d.options.map((o) => o.label.trim()).filter(Boolean);
    if (!d.field_name) e.field_name = 'Give the question a database name.';
    else if (!FIELD_NAME_PATTERN.test(d.field_name)) e.field_name = 'Use lowercase letters, numbers and underscores, starting with a letter (e.g. full_name).';
    else if (takenNames.has(d.field_name)) e.field_name = 'Another question already uses this database name.';
    if (d.field_type === 'file') {
      if (!d.allowed_file_types?.trim()) e.allowed_file_types = 'List the file types applicants can upload.';
      if (!d.max_file_size_mb) e.max_file_size_mb = 'Set the largest file size allowed.';
    }
    if (choice) {
      const min = d.is_workshop ? 1 : 2;
      if (labels.length < min) e.options = `Add at least ${min} option${min > 1 ? 's' : ''}.`;
      else if (new Set(labels).size !== labels.length) e.options = 'Two options have the same text.';
      else if (d.is_workshop && d.options.some((o) => o.label.trim() && !o.max_pax)) e.options = 'Set the number of places for every option.';
      if (d.field_type === 'checkbox' && !d.is_workshop) {
        const available = pickableCount(d);
        if (d.min_selections && d.min_selections > available) e.min_selections = `There are only ${available} options.`;
        if (d.max_selections && d.max_selections > available) e.max_selections = `There are only ${available} options.`;
        else if (d.min_selections && d.max_selections && d.min_selections > d.max_selections) e.max_selections = 'Can’t be less than the minimum.';
      }
    }
    return e;
  };

  const save = async () => {
    const found = validate(draft);
    setErrors(found);
    if (Object.keys(found).length) return;

    const editing = draft.field_id ? saved : null;
    const choice = isChoice(draft.field_type);
    const fieldData = {
      field_name: draft.field_name,
      display_label: draft.display_label.trim() || null,
      field_description: draft.field_description || null,
      field_type: draft.field_type,
      is_required: !!draft.is_required,
      field_order: editing ? editing.field_order : nextOrder,
    };
    const visible = draft.isVisible ? 1 : 0;
    if (!editing) fieldData.isVisible = visible;
    else if (visible !== (truthy(editing.isVisible) ? 1 : 0)) fieldData.isVisible = visible;

    if (draft.field_type === 'file') {
      fieldData.allowed_file_types = draft.allowed_file_types.trim();
      fieldData.max_file_size_mb = draft.max_file_size_mb;
    }
    let workshops = null;
    if (choice) {
      const options = draft.options.filter((o) => o.label.trim()).map((o) => ({ ...o, label: o.label.trim() }));
      fieldData.field_options = options.map((o) => o.label);
      fieldData.allow_other = draft.allow_other && !draft.is_workshop ? 1 : 0;
      fieldData.other_option_label = fieldData.allow_other ? (draft.other_option_label.trim() || 'Other') : null;
      fieldData.is_workshop = draft.is_workshop ? 1 : 0;
      if (draft.field_type === 'checkbox' && !draft.is_workshop) {
        fieldData.min_selections = draft.min_selections || null;
        fieldData.max_selections = draft.max_selections || null;
      }
      if (draft.is_workshop) {
        workshops = options.map((o) => {
          const existing = editing ? capacityFor(editing, o.label) : null;
          return existing?.workshop_id
            ? { option_label: o.label, max_pax: o.max_pax, workshop_id: existing.workshop_id }
            : { option_label: o.label, max_pax: o.max_pax };
        });
        fieldData.workshops = workshops;
      }
    }

    try {
      setSaving(true);
      const response = editing
        ? await dynamicFormsService.updateField(formId, editing.field_id, fieldData)
        : await dynamicFormsService.addField(formId, fieldData);
      if (!response.success) {
        message.error(response.error || 'Could not save the question');
        return;
      }
      const fieldId = editing ? editing.field_id : response.field?.field_id ?? response.field_id;
      if (workshops && fieldId) {
        const sync = await dynamicFormsService.syncWorkshops(formId, fieldId, workshops);
        if (!sync.success) message.warning('Question saved, but its places were not updated. Try saving again.');
      }
      const reloaded = await onFieldsChange();
      const fresh = (reloaded || []).find((f) => f.field_id === fieldId);
      setSelectedId(fieldId);
      setDraft(fresh ? draftFrom(fresh) : { ...draft, field_id: fieldId });
      setFocusField(draft.field_name);
      message.success(editing ? 'Question saved' : 'Question added');
    } catch (err) {
      message.error('Could not save the question');
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    if (!saved) return;
    modal.confirm({
      title: `Delete "${questionTitle(saved)}"?`,
      content: 'It disappears from the form straight away. Answers already sent to it are kept with their responses.',
      okText: 'Delete question',
      okButtonProps: { danger: true },
      rootClassName: 'do-scope',
      onOk: async () => {
        try {
          const response = await dynamicFormsService.deleteField(formId, saved.field_id);
          if (!response.success) {
            message.error(response.error || 'Could not delete the question');
            return;
          }
          const reloaded = (await onFieldsChange()) || [];
          const first = [...reloaded].sort((a, b) => a.field_order - b.field_order)[0];
          setSelectedId(first ? first.field_id : 'new');
          setDraft(first ? draftFrom(first) : null);
          setErrors({});
          message.success('Question deleted');
        } catch {
          message.error('Could not delete the question');
        }
      },
    });
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = sorted.findIndex((f) => f.field_id === active.id);
    const newIndex = sorted.findIndex((f) => f.field_id === over.id);
    const reordered = arrayMove(sorted, oldIndex, newIndex).map((f, i) => ({ ...f, field_order: i + 1 }));
    try {
      const response = await dynamicFormsService.updateFieldOrders(formId, reordered.map((f) => ({ field_id: f.field_id, field_order: f.field_order })));
      if (response && response.success === false) message.error(response.error || 'Could not change the order');
    } catch {
      message.error('Could not change the order');
    }
    await onFieldsChange();
  };

  // ---- the page as it will look, with the draft in place of (or after) the saved question
  const preview = useMemo(() => {
    let list = sorted.map(asPublicField);
    if (draft && draft.field_type) {
      const field = draftToPublicField(draft, saved, nextOrder);
      list = draft.field_id ? list.map((f) => (f.field_id === draft.field_id ? field : f)) : [...list, field];
    }
    return previewData({ language, layout, fields: list });
  }, [sorted, draft, saved, nextOrder, language, layout]);

  const mode = width >= 1180 ? 'wide' : width >= 720 ? 'mid' : 'narrow';
  const choice = draft && isChoice(draft.field_type);

  const editor = (
    <section className="dfw-editor" aria-label={draft?.field_id ? 'Edit question' : 'New question'}>
      {!canEdit && (
        <p className="dfw-note">You can look through the questions here. Changing them needs permission to manage client forms.</p>
      )}
      {selectedId === null && sorted.length === 0 && (
        <div className="dfw-empty">
          <strong>No questions yet</strong>
          <span>{canEdit ? 'Choose a question type to add the first one.' : 'This form has no questions.'}</span>
        </div>
      )}
      {(selectedId === 'new' || (selectedId === null && sorted.length === 0)) && canEdit && !draft && (
        <>
          <h3 className="dfw-editor__title">Add a question</h3>
          <p className="dfw-editor__sub">What kind of answer do you need?</p>
          <div className="dfw-types" role="list">
            {FIELD_TYPES.map((t) => (
              <button type="button" role="listitem" key={t.value} className="dfw-type" onClick={() => pickType(t.value)}>
                <span className="dfw-tile" aria-hidden="true">{t.icon}</span>
                <span className="dfw-type__text">
                  <strong>{t.label}</strong>
                  <small>{t.hint}</small>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {draft && (
        <fieldset className="dfw-editor__form" disabled={!canEdit || saving}>
          <div className="dfw-editor__head">
            <h3 className="dfw-editor__title">{draft.field_id ? 'Edit question' : 'New question'}</h3>
            {dirty && canEdit && <span className="dfw-badge">Not saved</span>}
          </div>

          <div className="dfw-field">
            <span className="dfw-label" id="dfw-type-label">Answer type</span>
            <div className="dfw-typechips" role="radiogroup" aria-labelledby="dfw-type-label">
              {FIELD_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.value}
                  role="radio"
                  aria-checked={draft.field_type === t.value}
                  className={`dfw-typechip${draft.field_type === t.value ? ' is-on' : ''}`}
                  onClick={() => pickType(t.value)}
                  disabled={!canEdit}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="dfw-field">
            <label className="dfw-label" htmlFor="dfw-q-label">Question</label>
            <Input
              id="dfw-q-label"
              value={draft.display_label}
              placeholder="e.g. Which workshops can you attend?"
              onChange={(e) => update({ display_label: e.target.value })}
            />
            {!draft.display_label.trim() && draft.field_name && (
              <p className="dfw-hint">Without a question the form shows “{formatFieldName(draft.field_name)}”.</p>
            )}
          </div>

          <div className="dfw-field">
            <span className="dfw-label" id="dfw-q-help-label">Help text <em>optional</em></span>
            <RichTextEditor
              value={draft.field_description}
              onChange={(v) => update({ field_description: v })}
              placeholder="Shown above the answer. Explain what you need, add a link or a short list."
              size="description"
              aria-label="Help text"
              readOnly={!canEdit}
            />
          </div>

          {choice && (
            <div className="dfw-field">
              <span className="dfw-label">{draft.is_workshop ? 'Options and places' : 'Options'}</span>
              <ol className="dfw-options">
                {draft.options.map((o, i) => (
                  <li key={i} className="dfw-option">
                    <span className="dfw-option__n" aria-hidden="true">{i + 1}</span>
                    <Input
                      value={o.label}
                      placeholder={`Option ${i + 1}`}
                      onChange={(e) => setOption(i, { label: e.target.value })}
                      aria-label={`Option ${i + 1}`}
                    />
                    {draft.is_workshop && (
                      <InputNumber
                        className="dfw-option__pax"
                        min={1}
                        max={10000}
                        value={o.max_pax}
                        onChange={(v) => setOption(i, { max_pax: v })}
                        placeholder="Places"
                        aria-label={`Places for option ${i + 1}`}
                      />
                    )}
                    <Tooltip title="Remove option">
                      <Button
                        type="text"
                        icon={<MinusCircleOutlined aria-hidden="true" />}
                        aria-label={`Remove option ${i + 1}`}
                        onClick={() => update({ options: draft.options.filter((_, k) => k !== i) })}
                        disabled={!canEdit || draft.options.length <= 1}
                      />
                    </Tooltip>
                  </li>
                ))}
              </ol>
              <Button type="dashed" icon={<PlusOutlined aria-hidden="true" />} onClick={() => update({ options: [...draft.options, { label: '', max_pax: null }] })} block>
                Add option
              </Button>
              <FieldError id="dfw-q-options-err" text={errors.options} />
            </div>
          )}

          {draft.field_type === 'file' && (
            <div className="dfw-row">
              <div className="dfw-field">
                <label className="dfw-label" htmlFor="dfw-q-types">File types</label>
                <Input
                  id="dfw-q-types"
                  value={draft.allowed_file_types}
                  placeholder="pdf,docx"
                  onChange={(e) => update({ allowed_file_types: e.target.value })}
                  status={errors.allowed_file_types ? 'error' : undefined}
                />
                <p className="dfw-hint">Extensions separated by commas.</p>
                <FieldError text={errors.allowed_file_types} />
              </div>
              <div className="dfw-field">
                <label className="dfw-label" htmlFor="dfw-q-size">Largest file</label>
                <InputNumber
                  id="dfw-q-size"
                  min={1}
                  max={100}
                  value={draft.max_file_size_mb}
                  onChange={(v) => update({ max_file_size_mb: v })}
                  addonAfter="MB"
                  status={errors.max_file_size_mb ? 'error' : undefined}
                  style={{ width: '100%' }}
                />
                <FieldError text={errors.max_file_size_mb} />
              </div>
            </div>
          )}

          <div className="dfw-toggles">
            <Toggle label="Required" hint="Applicants must answer" checked={draft.is_required} onChange={(v) => update({ is_required: v })} disabled={!canEdit} />
            <Toggle label="Show question title" hint="Turn off when the help text says it all" checked={draft.isVisible} onChange={(v) => update({ isVisible: v })} disabled={!canEdit} />
            {choice && (
              <Toggle
                label="Limit places"
                hint="Options close when they are full"
                checked={draft.is_workshop}
                onChange={(v) => update({ is_workshop: v, ...(v ? { allow_other: false } : {}) })}
                disabled={!canEdit}
              />
            )}
            {choice && (
              <Toggle
                label="“Other” answer"
                hint={draft.is_workshop ? 'Not available with limited places' : 'Applicants can write their own'}
                checked={draft.allow_other && !draft.is_workshop}
                onChange={(v) => update({ allow_other: v })}
                disabled={!canEdit || draft.is_workshop}
              />
            )}
          </div>

          {choice && draft.allow_other && !draft.is_workshop && (
            <div className="dfw-field">
              <label className="dfw-label" htmlFor="dfw-q-other">Name of the “Other” option</label>
              <Input id="dfw-q-other" value={draft.other_option_label} placeholder="Other" onChange={(e) => update({ other_option_label: e.target.value })} />
            </div>
          )}

          {draft.field_type === 'checkbox' && !draft.is_workshop && (
            <>
              <div className="dfw-row">
                <div className="dfw-field">
                  <label className="dfw-label" htmlFor="dfw-q-min">Minimum picks <em>optional</em></label>
                  <InputNumber
                    id="dfw-q-min"
                    min={1}
                    max={Math.max(1, pickableCount(draft))}
                    value={draft.min_selections}
                    onChange={(v) => update({ min_selections: v })}
                    placeholder="No minimum"
                    status={errors.min_selections ? 'error' : undefined}
                    style={{ width: '100%' }}
                  />
                  <FieldError text={errors.min_selections} />
                </div>
                <div className="dfw-field">
                  <label className="dfw-label" htmlFor="dfw-q-max">Maximum picks <em>optional</em></label>
                  <InputNumber
                    id="dfw-q-max"
                    min={1}
                    max={Math.max(1, pickableCount(draft))}
                    value={draft.max_selections}
                    onChange={(v) => update({ max_selections: v })}
                    placeholder="No maximum"
                    status={errors.max_selections ? 'error' : undefined}
                    style={{ width: '100%' }}
                  />
                  <FieldError text={errors.max_selections} />
                </div>
              </div>
              <p className="dfw-hint">
                {formStrings(ENGLISH).picks(draft.min_selections, draft.max_selections) || 'Applicants can pick any number.'} For exactly a number, set both to it.
              </p>
            </>
          )}

          <details className="dfw-advanced" open={!!errors.field_name || undefined}>
            <summary>Database name</summary>
            <div className="dfw-field">
              <label className="dfw-label" htmlFor="dfw-q-name">Database name</label>
              <Input
                id="dfw-q-name"
                value={draft.field_name}
                placeholder="e.g. full_name"
                onChange={(e) => update({ field_name: e.target.value, nameTouched: true })}
                status={errors.field_name ? 'error' : undefined}
              />
              <p className="dfw-hint">
                Used in exports and email settings.{draft.field_id ? ' Changing it after people have answered can break the email settings that use it.' : ' Filled in from the question.'}
              </p>
              <FieldError text={errors.field_name} />
            </div>
          </details>

          {canEdit && (
            <div className="dfw-editor__actions">
              {draft.field_id && (
                <Button danger type="text" icon={<DeleteOutlined aria-hidden="true" />} onClick={remove}>
                  Delete question
                </Button>
              )}
              <span className="dfw-grow" />
              {dirty && (
                <Button
                  onClick={() => {
                    setErrors({});
                    if (saved) setDraft(draftFrom(saved));
                    else if (sorted[0]) { setSelectedId(sorted[0].field_id); setDraft(draftFrom(sorted[0])); }
                    else setDraft(null);
                  }}
                >
                  {draft.field_id ? 'Undo changes' : 'Cancel'}
                </Button>
              )}
              <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>
                {draft.field_id ? 'Save question' : 'Add question'}
              </Button>
            </div>
          )}
        </fieldset>
      )}
    </section>
  );

  const outline = (
    <nav className="dfw-outline" aria-label="Questions">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sorted.map((f) => f.field_id)} strategy={verticalListSortingStrategy}>
          <ol className="dfw-outline__list">
            {sorted.map((f) => (
              <OutlineItem key={f.field_id} field={f} selected={f.field_id === selectedId} onSelect={() => select(f)} canEdit={canEdit} />
            ))}
            {selectedId === 'new' && draft && (
              <li className="dfw-outline__item is-on is-new">
                <span className="dfw-outline__pick">
                  <span className="dfw-tile" aria-hidden="true">{typeOf(draft.field_type).icon}</span>
                  <span className="dfw-outline__text">
                    <span className="dfw-outline__title">{draft.display_label || 'New question'}</span>
                    <span className="dfw-outline__type">{typeOf(draft.field_type).label} · not saved</span>
                  </span>
                </span>
              </li>
            )}
          </ol>
        </SortableContext>
      </DndContext>
      {canEdit && (
        <Button className="dfw-outline__add" type="dashed" icon={<PlusOutlined aria-hidden="true" />} onClick={startNew} block>
          Add a question
        </Button>
      )}
    </nav>
  );

  const livePreview = (
    <LivePreview
      data={preview}
      focusField={focusField}
      defaultDevice={mode === 'wide' ? 'phone' : 'desktop'}
    />
  );

  return (
    <div className={`dfw-builder dfw-builder--${mode}`} ref={rootRef}>
      <div className="dfw-builder__bar">
        <p className="dfw-builder__lede">
          {sorted.length} {sorted.length === 1 ? 'question' : 'questions'}. Saved changes are live on the form straight away.
        </p>
        <span className="dfw-grow" />
        {mode !== 'wide' && (
          <Button icon={<EyeOutlined aria-hidden="true" />} onClick={() => setPreviewOpen(true)}>Preview</Button>
        )}
        {liveUrl && (
          <Button href={liveUrl} target="_blank" rel="noopener noreferrer">Open live form</Button>
        )}
      </div>
      <div className="dfw-builder__cols">
        {outline}
        {editor}
        {mode === 'wide' && <div className="dfw-builder__preview">{livePreview}</div>}
      </div>
      {mode !== 'wide' && (
        <Drawer
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 24 : 720)}
          title="Preview"
          rootClassName="do-scope dfw-drawer"
          destroyOnHidden
        >
          <LivePreview data={preview} focusField={focusField} defaultDevice="phone" />
        </Drawer>
      )}
    </div>
  );
};

export default QuestionsBuilder;
